import React from 'react';

export type TypographyElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'label' | 'div';

export type TypographyVariant = 
  | 'heading-xl' 
  | 'heading-lg' 
  | 'heading-md' 
  | 'heading-sm' 
  | 'body-lg' 
  | 'body-md' 
  | 'body-sm' 
  | 'body-xs' 
  | 'editorial';

export type TypographyColor = 'main' | 'muted' | 'heading' | 'inverse' | 'accent' | 'danger' | 'inherit';

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  as?: TypographyElement;
  variant?: TypographyVariant;
  color?: TypographyColor;
  weight?: 'light' | 'regular' | 'medium' | 'semibold' | 'bold';
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const defaultElementMap: Record<TypographyVariant, TypographyElement> = {
  'heading-xl': 'h1',
  'heading-lg': 'h2',
  'heading-md': 'h3',
  'heading-sm': 'h4',
  'body-lg': 'p',
  'body-md': 'p',
  'body-sm': 'p',
  'body-xs': 'span',
  'editorial': 'span'
};

const variantClassMap: Record<TypographyVariant, string> = {
  'heading-xl': 'text-heading-xl',
  'heading-lg': 'text-heading-lg',
  'heading-md': 'text-heading-md',
  'heading-sm': 'text-heading-md',
  'body-lg': 'text-body-lg',
  'body-md': 'text-body-md',
  'body-sm': 'text-body-sm',
  'body-xs': 'text-body-xs',
  'editorial': 'editorial-label'
};

const colorStyleMap: Record<TypographyColor, string> = {
  main: 'var(--color-text-main)',
  muted: 'var(--color-text-muted)',
  heading: 'var(--color-text-heading)',
  inverse: 'var(--color-text-inverse)',
  accent: 'var(--color-accent-primary)',
  danger: 'var(--color-accent-danger)',
  inherit: 'inherit'
};

export default function Typography({
  as,
  variant = 'body-md',
  color,
  weight,
  children,
  className = '',
  style = {},
  ...rest
}: TypographyProps) {
  const Component = as || defaultElementMap[variant] || 'p';
  const variantClass = variantClassMap[variant] || 'text-body-md';

  const inlineStyles: React.CSSProperties = {
    ...(color ? { color: colorStyleMap[color] } : {}),
    ...(weight ? { fontWeight: `var(--font-weight-${weight})` } : {}),
    ...style
  };

  return (
    <Component
      className={`${variantClass} ${className}`.trim()}
      style={inlineStyles}
      {...rest}
    >
      {children}
    </Component>
  );
}
